import config from 'utils/config';

/**
 * True while Next.js is prerendering during `next build` (`NEXT_PHASE=phase-production-build`).
 *
 * Evaluated once at module load, so it reflects the process it runs in: `true` in the build
 * process, `false` in the serverless/edge runtime (separate processes, each loads the module
 * fresh). Reading it as a captured constant — rather than touching `process.env` inside an
 * `unstable_cache` callback — keeps cached functions clean.
 *
 * Use it to decide whether an upstream fetch should retry, and how hard. A prerender IS the
 * first cached response, so a failure it swallows is stored and re-served, and the guards
 * downstream refuse to finish such a render at all — which makes riding out a rate limit the
 * only thing between a 429 and a failed deploy. At runtime a failed ISR revalidation keeps
 * serving the last good entry instead (stale-while-error), so retries there are short and
 * explicit.
 *
 * Deliberately not qualified by the deploy target. An earlier cut of the build-429 work read
 * `VERCEL_ENV === 'preview'` on the theory that only preview builds are throttled, and a
 * *production* deploy then died on a 429 from `/v1/search/series` while prerendering
 * `/category/financials/biotech`. Production's limiter is looser than demo's, not absent.
 */
export const isBuildPhase = process.env.NEXT_PHASE === 'phase-production-build';

/**
 * True while a `next build` is prerendering against the shared **demo** backend.
 *
 * Not a mitigation, and deliberately not used as one — every lever that decides how a request
 * behaves keys on `isBuildPhase` alone. This decides one thing: **who gets paged**.
 *
 * `logError` reaches `console.error`, which Datadog alerts on. A throttled production build is
 * worth alerting on — it is the only advance warning that the deploy pipeline is being rate
 * limited, before a request runs out of budget and fails the build outright. A throttled
 * demo-backed build is not: demo is shared, the load is usually someone else's, and with seven
 * retries per request an error per retry would flood the channel from every preview of every
 * PR. Those builds still fail loudly if the budget runs out; this only declines to page anyone
 * while it is being spent.
 *
 * Covers all three demo-backed builds, not just Vercel previews — `npm run e2e:build` sets
 * `NEXT_PUBLIC_BACKEND_ENVIRONMENT=demo` and no `VERCEL_ENV` at all, and a local
 * `npm run build-demo` sets the same.
 *
 * A function, not a constant, because it is the only flag here that dereferences an *imported*
 * object. `isBuildPhase` reads `process.env`, which is always there; this reads
 * `config.backendEnv`, and doing that at module scope evaluates an imported binding during
 * this module's own initialization — the module-scope deref this repo has been bitten by
 * before (`fetchServerSide`'s `requestTimeoutMs` defers a read for the same reason).
 * `utils/config` imports nothing that leads back here today, so a constant would be correct
 * today; the point is that it stays correct without anyone having to re-verify that. The read
 * is a property access on a frozen module object — call it freely.
 */
export const isDemoBackedBuild = (): boolean => isBuildPhase && config.backendEnv === 'demo';
